import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Shared tab bar — the one true tab style for the app.
 * Design system: Lodge Orange is the only active accent (never blue/indigo).
 *
 * Props:
 *   tabs:     [{ key, label, icon?: LucideComponent, badge?: node, disabled?: bool }]
 *   value:    key of the active tab
 *   onChange: (key) => void
 *   variant:  'underline' (default — orange underline) | 'pill' (orange-filled active)
 */
export function Tabs({ tabs = [], value, onChange, variant = 'underline', className, ariaLabel }) {
  const isPill = variant === 'pill';

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex items-center',
        isPill
          ? 'gap-1 p-1 rounded-lg bg-[var(--lodge-onyx-100)] w-fit'
          : 'gap-1 border-b border-[var(--lodge-onyx-100)]',
        className
      )}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const active = tab.key === value;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={tab.disabled}
            onClick={() => !tab.disabled && onChange?.(tab.key)}
            className={cn(
              'inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none',
              isPill
                ? cn(
                    'px-4 py-1.5 rounded-md',
                    active
                      ? 'bg-[var(--lodge-orange-400)] text-white shadow-sm'
                      : 'text-[var(--lodge-onyx-600)] hover:bg-[var(--lodge-onyx-50)]'
                  )
                : cn(
                    'px-1 pb-2.5 pt-1 -mb-px border-b-2',
                    active
                      ? 'border-[var(--lodge-orange-400)] text-[var(--lodge-onyx-900)] font-semibold'
                      : 'border-transparent text-[var(--lodge-onyx-500)] hover:text-[var(--lodge-onyx-700)]'
                  )
            )}
          >
            {Icon && <Icon size={16} className="shrink-0" />}
            <span>{tab.label}</span>
            {tab.badge != null && <span className="ml-1">{tab.badge}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
